/* eslint-disable max-lines -- attendee table and edit dialog share one screen contract */
import * as React from 'react'
import { ArrowUp, ArrowUpDown, Search } from 'lucide-react'
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { ApiError, getJSON, patchJSON } from '@/lib/api'
import { cn } from '@/lib/utils'
import { OwnedChip } from '@/components/OwnedChip'
import { OwnerFilter } from '@/components/OwnerFilter'

type EventAttendee = {
  id: number
  seat: number
  name: string | null
  email: string | null
  buyer: { id: number; name: string }
  ticket_type: { id: number; name: string }
  answers: Record<string, string>
  owner?: import('@/components/OwnedChip').Owner | string | null
}

type AttendeeBook = {
  attendees: EventAttendee[]
  attendee_columns: { id: number; prompt: string }[]
  attendee_ticket_types: { id: number; name: string }[]
  attendee_summary: { named: number; unnamed: number }
}

export function EventAttendees({
  eventId,
  readOnly = false,
}: {
  eventId: number
  readOnly?: boolean
}) {
  const [search, setSearch] = React.useState('')
  const [ticket, setTicket] = React.useState('all')
  const [named, setNamed] = React.useState('all')
  const [owner, setOwner] = React.useState<string | undefined>()
  const [sort, setSort] = React.useState<'name' | 'ticket'>('name')
  const [direction, setDirection] = React.useState<'asc' | 'desc'>('asc')
  const [editing, setEditing] = React.useState<EventAttendee | null>(null)
  const query = useQuery({
    queryKey: ['event-attendees', eventId, search, ticket, named, owner, sort, direction],
    queryFn: () =>
      getJSON<AttendeeBook>(
        attendeeBookPath(eventId, { search, ticket, named, owner, sort, direction }),
      ),
    retry: false,
  })

  return (
    <>
      <AttendeeToolbar
        search={search}
        ticket={ticket}
        named={named}
        ticketTypes={query.data?.attendee_ticket_types ?? []}
        onSearch={setSearch}
        onTicket={setTicket}
        onNamed={setNamed}
        owner={owner}
        onOwner={setOwner}
      />
      <AttendeesTable
        query={query}
        sort={sort}
        direction={direction}
        onSort={(nextSort) => {
          if (nextSort === sort) setDirection((current) => (current === 'asc' ? 'desc' : 'asc'))
          else {
            setSort(nextSort)
            setDirection('asc')
          }
        }}
        onEdit={readOnly ? undefined : setEditing}
      />
      {editing && (
        <AttendeeEditDialog
          key={editing.id}
          eventId={eventId}
          attendee={editing}
          onClose={() => setEditing(null)}
        />
      )}
    </>
  )
}

function AttendeeToolbar({
  search,
  ticket,
  named,
  ticketTypes,
  onSearch,
  onTicket,
  onNamed,
  owner,
  onOwner,
}: {
  search: string
  ticket: string
  named: string
  ticketTypes: { id: number; name: string }[]
  onSearch: (value: string) => void
  onTicket: (value: string) => void
  onNamed: (value: string) => void
  owner?: string
  onOwner: (value?: string) => void
}) {
  return (
    <div className="mb-3 flex flex-wrap items-center gap-2">
      <div className="relative w-full max-w-xs">
        <Search
          className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground"
          aria-hidden="true"
        />
        <Input
          className="h-8 pl-8 text-caption"
          placeholder="Search attendee"
          value={search}
          onChange={(event) => onSearch(event.target.value)}
        />
      </div>
      <Select value={ticket} onValueChange={onTicket}>
        <SelectTrigger aria-label="Ticket" className="h-8 w-44 text-caption">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Ticket · All</SelectItem>
          {ticketTypes.map((ticketType) => (
            <SelectItem key={ticketType.id} value={String(ticketType.id)}>
              {ticketType.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <OwnerFilter value={owner} onChange={onOwner} />
      <Select value={named} onValueChange={onNamed}>
        <SelectTrigger aria-label="Named" className="h-8 w-36 text-caption">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Named · All</SelectItem>
          <SelectItem value="true">Named</SelectItem>
          <SelectItem value="false">Unnamed</SelectItem>
        </SelectContent>
      </Select>
    </div>
  )
}

function AttendeesTable({
  query,
  sort,
  direction,
  onSort,
  onEdit,
}: {
  query: UseQueryResult<AttendeeBook>
  sort: 'name' | 'ticket'
  direction: 'asc' | 'desc'
  onSort: (sort: 'name' | 'ticket') => void
  onEdit?: (attendee: EventAttendee) => void
}) {
  if (query.isLoading) return <TableMessage>Loading attendees…</TableMessage>
  if (query.isError) return <TableMessage>Attendees could not be loaded.</TableMessage>
  const attendees = query.data?.attendees ?? []
  const columns = query.data?.attendee_columns ?? []
  const summary = query.data?.attendee_summary ?? { named: 0, unnamed: 0 }

  return (
    <div className="space-y-2">
      <p className="text-caption text-muted-foreground">
        {summary.named} named · {summary.unnamed} unnamed
      </p>
      <div className="overflow-hidden rounded-md border border-border bg-background">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>
                <SortHeader
                  label="Attendee"
                  active={sort === 'name'}
                  direction={direction}
                  onClick={() => onSort('name')}
                />
              </TableHead>
              <TableHead>
                <SortHeader
                  label="Ticket"
                  active={sort === 'ticket'}
                  direction={direction}
                  onClick={() => onSort('ticket')}
                />
              </TableHead>
              <TableHead>Buyer</TableHead>
              {columns.map((column) => (
                <TableHead key={column.id}>{column.prompt}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {attendees.map((attendee) => (
              <TableRow key={attendee.id} {...editableRowProps(attendee, onEdit)}>
                <TableCell>
                  <p
                    className={cn(
                      'font-medium',
                      !attendee.name && 'font-normal text-muted-foreground',
                    )}
                  >
                    {attendee.name ?? `Unnamed · seat ${attendee.seat}`}{' '}
                    <OwnedChip owner={attendee.owner} />
                  </p>
                  {attendee.email && (
                    <p className="text-caption text-muted-foreground">{attendee.email}</p>
                  )}
                </TableCell>
                <TableCell>{attendee.ticket_type.name}</TableCell>
                <TableCell className="text-caption text-muted-foreground">
                  {attendee.buyer.name}
                </TableCell>
                {columns.map((column) => (
                  <TableCell key={column.id} className="text-caption text-muted-foreground">
                    {attendee.answers[String(column.id)] || '—'}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {attendees.length === 0 && (
          <p className="px-4 py-12 text-center text-caption text-muted-foreground">
            No attendees found.
          </p>
        )}
      </div>
    </div>
  )
}

function AttendeeEditDialog({
  eventId,
  attendee,
  onClose,
}: {
  eventId: number
  attendee: EventAttendee
  onClose: () => void
}) {
  const queryClient = useQueryClient()
  const [name, setName] = React.useState(attendee.name ?? '')
  const [email, setEmail] = React.useState(attendee.email ?? '')
  const update = useMutation({
    mutationFn: () =>
      patchJSON<{ attendee: Pick<EventAttendee, 'id' | 'name' | 'email'> }>(
        `/api/v1/events/${eventId}/attendees/${attendee.id}`,
        { attendee: { name, email } },
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['event-attendees', eventId] })
      onClose()
    },
  })

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <form
          onSubmit={(event) => {
            event.preventDefault()
            update.mutate()
          }}
        >
          <DialogHeader>
            <DialogTitle>Edit Attendee</DialogTitle>
            <DialogDescription>
              Update this seat’s personal details. Clearing both fields keeps the seat, Order, and
              money history intact.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-4">
            <div className="space-y-1.5">
              <Label htmlFor={`attendee-${attendee.id}-name`}>Name</Label>
              <Input
                id={`attendee-${attendee.id}-name`}
                value={name}
                onChange={(event) => setName(event.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor={`attendee-${attendee.id}-email`}>Email</Label>
              <Input
                id={`attendee-${attendee.id}-email`}
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            </div>
            {update.isError && (
              <p className="text-caption text-destructive" role="alert">
                {attendeeUpdateError(update.error)}
              </p>
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={update.isPending}>
              {update.isPending ? 'Saving…' : 'Save changes'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function attendeeUpdateError(error: Error) {
  if (error instanceof ApiError && hasEmailErrors(error.body)) {
    return `Email ${error.body.errors.email.join(', ')}`
  }
  return 'Attendee changes could not be saved.'
}

function hasEmailErrors(body: unknown): body is { errors: { email: string[] } } {
  if (typeof body !== 'object' || body === null || !('errors' in body)) return false
  const errors = (body as { errors?: unknown }).errors
  if (typeof errors !== 'object' || errors === null || !('email' in errors)) return false
  return Array.isArray((errors as { email?: unknown }).email)
}

function SortHeader({
  label,
  active,
  direction,
  onClick,
}: {
  label: string
  active: boolean
  direction: 'asc' | 'desc'
  onClick: () => void
}) {
  const Icon = active ? ArrowUp : ArrowUpDown
  return (
    <button
      type="button"
      className={cn(
        'inline-flex items-center gap-1 text-caption font-normal uppercase tracking-wide text-muted-foreground hover:text-foreground',
        active && 'text-foreground',
      )}
      onClick={onClick}
    >
      {label}
      <Icon
        className={cn('size-3', active && direction === 'desc' && 'rotate-180')}
        strokeWidth={1.5}
        aria-hidden="true"
      />
    </button>
  )
}

function TableMessage({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-md border border-dashed border-border px-4 py-12 text-center text-caption text-muted-foreground">
      {children}
    </div>
  )
}

function attendeeBookPath(
  eventId: number,
  filters: {
    search: string
    ticket: string
    named: string
    owner?: string
    sort: string
    direction: string
  },
) {
  const parameters = new URLSearchParams()
  if (filters.search.trim()) parameters.set('attendee_search', filters.search.trim())
  if (filters.ticket !== 'all') parameters.set('attendee_ticket_type_id', filters.ticket)
  if (filters.named !== 'all') parameters.set('attendee_named', filters.named)
  if (filters.owner) parameters.set('owner', filters.owner)
  if (filters.sort !== 'name' || filters.direction !== 'asc') {
    parameters.set('attendee_sort', filters.sort)
    parameters.set('attendee_direction', filters.direction)
  }
  const query = parameters.toString()
  return query ? `/api/v1/events/${eventId}/orders?${query}` : `/api/v1/events/${eventId}/orders`
}

function editableRowProps(attendee: EventAttendee, onEdit?: (attendee: EventAttendee) => void) {
  if (!onEdit || isOwned(attendee)) return {}
  return {
    'aria-label': `Edit Attendee ${attendee.name ?? `seat ${attendee.seat}`}`,
    className: 'cursor-pointer',
    tabIndex: 0,
    onClick: () => onEdit(attendee),
    onKeyDown: (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') onEdit(attendee)
    },
  }
}

function isOwned(attendee: EventAttendee) {
  return Boolean(attendee.owner && typeof attendee.owner !== 'string' && attendee.owner.connection)
}
